import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faMagnifyingGlass,
  faSpinnerThird,
} from '@fortawesome/sharp-solid-svg-icons';
import { Button } from 'eone-ui/buttons/button';
import { InputString } from 'eone-ui/inputs/input-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SCButton } from '../button';
import { Pagination } from '../pagination';
import './index.css';

export interface ExpandableTableColumn {
  Label: string;
  FieldName: string;
  FieldType?: 'String' | 'Number' | 'Date' | 'Boolean';
  Sortable?: boolean;
  Width?: string;
  RenderRow?: (props: {
    Row: any;
    ColumnIndex: number;
    Column: ExpandableTableColumn;
  }) => React.ReactNode;
}

export interface ExpandableTableRowButton {
  Label: string;
  Title: string;
  Icon: IconDefinition;
  Cancel?: boolean;
  OnClick: (id: string) => void;
}

export interface ExpandableTableHeaderButton {
  Label: string;
  Title?: string;
  Icon?: IconDefinition;
  OnClick?: () => void;
  Disabled?: boolean;
  Primary?: boolean;
  Tertiary?: boolean;
  DataTestId?: string;
}

export interface PaginationConfig {
  enabled: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  onChange?: (page: number, pageSize: number, rowId?: string) => void;
}

export interface ExpandableTableProps<TMainRow, TSubRow> {
  Title?: string;
  Icon?: IconDefinition;
  Loading?: boolean;
  MainColumns: ExpandableTableColumn[];
  SubColumns: ExpandableTableColumn[];
  MainRows: TMainRow[];
  GetSubRows: (mainRow: TMainRow) => TSubRow[];
  GetMainRowId: (mainRow: TMainRow) => string;
  GetSubRowId: (subRow: TSubRow) => string;
  MainRowButtons?: ExpandableTableRowButton[];
  SubRowButtons?: ExpandableTableRowButton[];
  HeaderButtons?: ExpandableTableHeaderButton[];
  TableHeaderButtons?: ExpandableTableHeaderButton[];
  EmptyIcon?: any;
  EmptyTitle?: string;
  EmptyMessage?: string;
  Search?: boolean;
  BodyHeight?: string | number;
  MainPagination?: PaginationConfig;
  SubPagination?: PaginationConfig;
  onMainRowClick?: (row: TMainRow) => void;
  onSubRowClick?: (row: TSubRow) => void;
  onExpandRow?: (row: TMainRow, rowId: string) => void;
  ExpandedRowIds?: string[];
}

export function ExpandableTable<
  TMainRow extends Record<string, any>,
  TSubRow extends Record<string, any>,
>({
  Title,
  Icon,
  Loading = false,
  MainColumns,
  SubColumns,
  MainRows,
  GetSubRows,
  GetMainRowId,
  GetSubRowId,
  MainRowButtons = [],
  SubRowButtons = [],
  HeaderButtons = [],
  TableHeaderButtons = [],
  EmptyTitle,
  EmptyIcon,
  EmptyMessage = 'No data found',
  Search = false,
  BodyHeight,
  MainPagination,
  SubPagination,
  onMainRowClick,
  onSubRowClick,
  onExpandRow,
  ExpandedRowIds,
}: ExpandableTableProps<TMainRow, TSubRow>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(ExpandedRowIds || []));
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!ExpandedRowIds || ExpandedRowIds.length === 0) return;
    setExpandedRows((prev) => {
      const next = new Set(prev);
      ExpandedRowIds.forEach((id) => next.add(id));
      return next;
    });
  }, [ExpandedRowIds]);

  // Main table pagination state
  const [mainCurrentPage, setMainCurrentPage] = useState(1);
  const [mainPageSize, setMainPageSize] = useState(
    MainPagination?.pageSize || MainPagination?.pageSizeOptions?.[0] || 10,
  );

  // Main table pagination handlers with onChange callback
  const handleMainPageChange = useCallback(
    (page: number) => {
      setMainCurrentPage(page);
      MainPagination?.onChange?.(page, mainPageSize);
    },
    [MainPagination, mainPageSize],
  );

  const handleMainPageSizeChange = useCallback(
    (pageSize: number) => {
      setMainPageSize(pageSize);
      setMainCurrentPage(1); // Reset to first page
      MainPagination?.onChange?.(1, pageSize);
    },
    [MainPagination],
  );

  // Sub table pagination state - each sub table has its own page state
  const [subTablePages, setSubTablePages] = useState<Record<string, number>>({});
  const [subTablePageSizes, setSubTablePageSizes] = useState<Record<string, number>>({});

  const toggleRowExpansion = useCallback(
    (rowId: string, row: TMainRow) => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(rowId)) {
          newSet.delete(rowId);
        } else {
          newSet.add(rowId);
          // Call onExpandRow when expanding a row for the first time
          if (onExpandRow) {
            onExpandRow(row, rowId);
          }
        }
        return newSet;
      });
    },
    [onExpandRow],
  );

  const filteredMainRows = useMemo(() => {
    if (!Search || !searchTerm) return MainRows;

    return MainRows.filter((row) => {
      return MainColumns.some((column) => {
        const value = row[column.FieldName];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [MainRows, MainColumns, searchTerm, Search]);

  // Paginated main rows
  const paginatedMainRows = useMemo(() => {
    if (!MainPagination?.enabled) return filteredMainRows;

    const startIndex = (mainCurrentPage - 1) * mainPageSize;
    const endIndex = startIndex + mainPageSize;
    return filteredMainRows.slice(startIndex, endIndex);
  }, [filteredMainRows, MainPagination, mainCurrentPage, mainPageSize]);

  const totalMainPages = useMemo(() => {
    if (!MainPagination?.enabled) return 1;
    return Math.ceil(filteredMainRows.length / mainPageSize);
  }, [filteredMainRows.length, mainPageSize, MainPagination]);

  // Get paginated sub rows for a specific main row
  const getPaginatedSubRows = useCallback(
    (rowId: string, subRows: TSubRow[]) => {
      if (!SubPagination?.enabled) return subRows;

      const currentPage = subTablePages[rowId] || 1;
      const pageSize =
        subTablePageSizes[rowId] ||
        SubPagination?.pageSize ||
        SubPagination?.pageSizeOptions?.[0] ||
        10;

      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return subRows.slice(startIndex, endIndex);
    },
    [SubPagination, subTablePages, subTablePageSizes],
  );

  const renderCell = useCallback(
    (row: any, column: ExpandableTableColumn, columnIndex: number, isSubRow: boolean = false) => {
      if (column.RenderRow) {
        return column.RenderRow({ Row: row, ColumnIndex: columnIndex, Column: column });
      }

      const value = row[column.FieldName];
      const cellStyle = isSubRow ? { paddingLeft: '20px' } : {};

      return <span style={cellStyle}>{value || '-'}</span>;
    },
    [],
  );

  const renderRowButtons = useCallback((buttons: ExpandableTableRowButton[], rowId: string) => {
    return (
      <td className="expandable-table-actions">
        <div className="expandable-table-actions-container">
          {buttons.map((button, index) =>
            button.Cancel ? (
              <div
                key={index}
                className="eone-ui-row-button-danger"
                title={button.Title}
                onClick={() => button.OnClick(rowId)}
              >
                <FontAwesomeIcon icon={button.Icon} />
              </div>
            ) : (
              <button
                key={index}
                title={button.Title}
                onClick={() => button.OnClick(rowId)}
                className="eone-ui-table-row-button"
              >
                <FontAwesomeIcon icon={button.Icon} />
              </button>
            ),
          )}
        </div>
      </td>
    );
  }, []);

  const renderMainRow = useCallback(
    (row: TMainRow) => {
      const rowId = GetMainRowId(row);
      const isExpanded = expandedRows.has(rowId);
      const subRows = GetSubRows(row);
      const hasSubRows = subRows && subRows.length > 0;

      // Get paginated sub rows and pagination info
      const paginatedSubRows = getPaginatedSubRows(rowId, subRows);
      const currentSubPage = subTablePages[rowId] || 1;
      const currentSubPageSize =
        subTablePageSizes[rowId] ||
        SubPagination?.pageSize ||
        SubPagination?.pageSizeOptions?.[0] ||
        10;
      const totalSubPages = SubPagination?.enabled
        ? Math.ceil(subRows.length / currentSubPageSize)
        : 1;

      const rowClass = isExpanded ? 'eone-ui-table-row expanded' : 'eone-ui-table-row';

      return (
        <React.Fragment key={rowId}>
          {/* Main Row */}
          <tr className={rowClass} onClick={() => onMainRowClick?.(row)}>
            {/* Expand button column */}
            <td className="eone-ui-table-row-data">
              {hasSubRows && (
                <SCButton
                  onClick={() => toggleRowExpansion(rowId, row)}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                  icon={isExpanded ? faChevronDown : faChevronRight}
                />
              )}
            </td>
            {MainColumns.map((column, columnIndex) => (
              <td key={column.FieldName} className="eone-ui-table-row-data">
                {renderCell(row, column, columnIndex, false)}
              </td>
            ))}
            {MainRowButtons.length > 0 && renderRowButtons(MainRowButtons, rowId)}
          </tr>

          {/* Sub Table */}
          {hasSubRows && isExpanded && (
            <tr className="expandable-table-sub-table-row">
              <td
                colSpan={MainColumns.length + 1 + (MainRowButtons.length > 0 ? 1 : 0)}
                className="expandable-table-sub-table-container"
              >
                <div className="expandable-table-sub-table">
                  <table className="eone-ui-table">
                    <thead>
                      <tr className="eone-ui-table-row-header">
                        {SubColumns.map((column) => (
                          <th key={column.FieldName} className="expandable-table-sub-header-cell">
                            <span className="eone-ui-table-header">{column.Label}</span>
                          </th>
                        ))}
                        {SubRowButtons.length > 0 && (
                          <th className="expandable-table-sub-header-cell expandable-table-actions-header" />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubRows.map((subRow) => {
                        const subRowId = GetSubRowId(subRow);

                        // Check if this is a placeholder row
                        if ((subRow as any).__placeholder) {
                          return (
                            <tr key={subRowId} className="eone-ui-table-row">
                              <td
                                colSpan={SubColumns.length + (SubRowButtons.length > 0 ? 1 : 0)}
                                className="expandable-table-sub-cell"
                                style={{ textAlign: 'center', padding: '20px', color: '#666' }}
                              >
                                <div className="eone-ui-table-loading" style={{ height: '100%' }}>
                                  <div>
                                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                                    <label className="eone-ui-table-loading-prompt">
                                      Loading...
                                    </label>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr
                            key={subRowId}
                            className="eone-ui-table-row"
                            onClick={() => onSubRowClick?.(subRow)}
                          >
                            {SubColumns.map((column, columnIndex) => (
                              <td key={column.FieldName} className="eone-ui-table-row-data">
                                {renderCell(subRow, column, columnIndex, true)}
                              </td>
                            ))}
                            {SubRowButtons.length > 0 && renderRowButtons(SubRowButtons, subRowId)}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Sub Table Pagination */}
                  {SubPagination?.enabled && subRows.length > 0 && (
                    <Pagination
                      currentPage={currentSubPage}
                      totalPages={totalSubPages}
                      pageSize={currentSubPageSize}
                      pageSizeOptions={SubPagination.pageSizeOptions}
                      totalItems={subRows.length}
                      onPageChange={(page) => {
                        const prevPage = subTablePages[rowId] || 1;
                        // Only call onChange if page actually changed
                        if (page !== prevPage) {
                          setSubTablePages((prev) => ({ ...prev, [rowId]: page }));
                          SubPagination?.onChange?.(page, currentSubPageSize, rowId);
                        }
                      }}
                      onPageSizeChange={(size) => {
                        setSubTablePageSizes((prev) => ({ ...prev, [rowId]: size }));
                        setSubTablePages((prev) => ({ ...prev, [rowId]: 1 })); // Reset to first page
                        SubPagination?.onChange?.(1, size, rowId);
                      }}
                    />
                  )}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    },
    [
      MainColumns,
      SubColumns,
      MainRowButtons,
      SubRowButtons,
      SubPagination,
      expandedRows,
      subTablePages,
      subTablePageSizes,
      GetMainRowId,
      GetSubRowId,
      GetSubRows,
      toggleRowExpansion,
      renderCell,
      renderRowButtons,
      getPaginatedSubRows,
      onMainRowClick,
      onSubRowClick,
    ],
  );

  const getHeaderButtonClass = (b: ExpandableTableHeaderButton) =>
    b.Tertiary
      ? 'eone-ui-button-container-tertiary'
      : b.Primary
        ? 'eone-ui-button-container-primary'
        : 'eone-ui-button-container-secondary';

  const showHeader = Boolean(Title || HeaderButtons.length > 0 || Search);

  return (
    <div className="eone-ui-panel" style={{ height: 0, minHeight: 0 }}>
      {showHeader && (
        <div className="eone-ui-panel-heading-container">
          {Title && (
            <div>
              {Icon && <FontAwesomeIcon icon={Icon} className="expandable-table-header-icon" />}
              <span className="eone-ui-panel-heading-label">{Title}</span>
            </div>
          )}
          {HeaderButtons.length > 0 && (
            <div className="eone-ui-panel-header-controls-container">
              {HeaderButtons.map((b, i) => (
                <Button
                  key={i}
                  Class={getHeaderButtonClass(b)}
                  Label={b.Label}
                  Title={b.Title || b.Label}
                  Icon={b.Icon}
                  Disabled={b.Disabled}
                  OnClick={b.OnClick}
                  DataTestId={b.DataTestId}
                />
              ))}
            </div>
          )}
        </div>
      )}
      <div
        className="expandable-table-master-container"
        style={BodyHeight ? { height: BodyHeight } : undefined}
      >
        {Loading ? (
          <div className="eone-ui-table-loading" style={{ height: '100%' }}>
            <div>
              <FontAwesomeIcon icon={faSpinnerThird} spin />
              <label className="eone-ui-table-loading-prompt">Loading...</label>
            </div>
          </div>
        ) : paginatedMainRows.length === 0 ? (
          <div className="expandable-table-empty">
            {EmptyIcon && (
              <FontAwesomeIcon icon={EmptyIcon} className="expandable-table-header-icon" />
            )}
            {EmptyTitle && <div className="expandable-table-empty-title">{EmptyTitle}</div>}
            <div className="expandable-table-empty-prompt">{EmptyMessage}</div>
          </div>
        ) : (
          <div
            className={
              BodyHeight
                ? 'expandable-table-container expandable-table-container-with-height'
                : 'expandable-table-container'
            }
          >
            <div className="eone-ui-table-container-header">
              {Search ? (
                <InputString
                  Icon={faMagnifyingGlass}
                  Placeholder="Search"
                  Value={searchTerm}
                  OnChange={setSearchTerm}
                />
              ) : (
                <div />
              )}
              {TableHeaderButtons.length > 0 && (
                <div className="eone-ui-table-container-header-buttons">
                  {TableHeaderButtons.map((b, i) => (
                    <Button
                      key={i}
                      Class="eone-ui-button-container-tertiary"
                      Visible
                      Label={b.Label}
                      Title={b.Title || b.Label}
                      Icon={b.Icon}
                      Disabled={b.Disabled}
                      OnClick={b.OnClick}
                      DataTestId={b.DataTestId}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="expandable-table-scroll-area">
              <table className="eone-ui-table">
                <thead>
                  <tr className="eone-ui-table-row-header">
                    {/* Expand column */}
                    <th className="eone-ui-table-header-data eone-ui-table-header-spacer">
                      <div className="eone-ui-table-header-container" />
                    </th>
                    {MainColumns.map((column) => (
                      <th key={column.FieldName} className="eone-ui-table-header-data">
                        <span className="eone-ui-table-header">{column.Label}</span>
                      </th>
                    ))}
                    {MainRowButtons.length > 0 && (
                      <th className="expandable-table-header-cell expandable-table-actions-header">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>{paginatedMainRows.map((row) => renderMainRow(row))}</tbody>
              </table>
            </div>

            {/* Main Table Pagination */}
            {MainPagination?.enabled && filteredMainRows.length > 0 && (
              <Pagination
                currentPage={mainCurrentPage}
                totalPages={totalMainPages}
                pageSize={mainPageSize}
                pageSizeOptions={MainPagination.pageSizeOptions}
                totalItems={filteredMainRows.length}
                onPageChange={handleMainPageChange}
                onPageSizeChange={handleMainPageSizeChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpandableTable;
